import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api';
import { useCan } from '../../lib/session';
import type { AuditLogDetail, AuditLogListItem, OverrideReportRow } from './types';
import { SkeletonBlock, SkeletonRows } from '../skeleton';

const ROW_HEIGHT = 44;
const PAGE_SIZE = 100;

interface Filters {
  entityType: string;
  actorName: string;
  authorizerName: string;
  action: string;
  occurredFrom: string;
  occurredTo: string;
}

const EMPTY_FILTERS: Filters = {
  entityType: '',
  actorName: '',
  authorizerName: '',
  action: '',
  occurredFrom: '',
  occurredTo: '',
};

function buildQuery(filters: Filters, extra: Record<string, string>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
  for (const [key, value] of Object.entries(extra)) if (value) params.set(key, value);
  return params.toString();
}

/** 审计日志查询（M11 新增，D-31）。筛选走姓名文本匹配，不做用户选择器（见 D-31 第 2 条）。 */
export function AuditLogPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<AuditLogListItem | null>(null);
  const [showOverrideReport, setShowOverrideReport] = useState(false);
  // 【审计双版本】持全量码（顶尖）看系统级事件与 override 报告；仅运营码则显示提示
  const hasFullAudit = useCan(PERMISSIONS.AUDIT_VIEW);
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['audit-log', filters],
    queryFn: ({ pageParam }) =>
      apiFetch<{ items: AuditLogListItem[]; nextCursor: string | null }>(
        `/audit-log?${buildQuery(filters, { limit: String(PAGE_SIZE), cursor: pageParam })}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data]);

  const parentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const onScroll = (): void => {
      const lastIndex = Math.floor((el.scrollTop + el.clientHeight) / ROW_HEIGHT);
      if (lastIndex >= items.length - 10 && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [items.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div>
      <div className="row row--between">
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('nav.auditLog')}</h1>
        {/* 【审计双版本】override 报告属系统级（提权/授权），全量码专属 */}
        {hasFullAudit && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowOverrideReport(true)}>
            {t('audit.overrideReportButton')}
          </button>
        )}
      </div>
      {!hasFullAudit && (
        <p className="field-hint">{t('audit.operationalBanner')}</p>
      )}

      <div style={{ margin: 'var(--space-3) 0' }}>
        <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="field" style={{ maxWidth: '160px' }}>
            <span>{t('audit.filterEntityType')}</span>
            <input value={draft.entityType} onChange={(e) => setDraft((d) => ({ ...d, entityType: e.target.value }))} />
          </label>
          <label className="field" style={{ maxWidth: '160px' }}>
            <span>{t('audit.filterAction')}</span>
            <input value={draft.action} onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))} />
          </label>
          <label className="field" style={{ maxWidth: '160px' }}>
            <span>{t('audit.filterActor')}</span>
            <input value={draft.actorName} onChange={(e) => setDraft((d) => ({ ...d, actorName: e.target.value }))} />
          </label>
          <label className="field" style={{ maxWidth: '160px' }}>
            <span>{t('audit.filterAuthorizer')}</span>
            <input
              value={draft.authorizerName}
              onChange={(e) => setDraft((d) => ({ ...d, authorizerName: e.target.value }))}
            />
          </label>
          <label className="field" style={{ maxWidth: '160px' }}>
            <span>{t('audit.filterFrom')}</span>
            <input
              type="date"
              value={draft.occurredFrom}
              onChange={(e) => setDraft((d) => ({ ...d, occurredFrom: e.target.value }))}
            />
          </label>
          <label className="field" style={{ maxWidth: '160px' }}>
            <span>{t('audit.filterTo')}</span>
            <input
              type="date"
              value={draft.occurredTo}
              onChange={(e) => setDraft((d) => ({ ...d, occurredTo: e.target.value }))}
            />
          </label>
        </div>
        {/* 【用户反馈】"应用筛选跟清除筛选错位了"——两个按钮此前跟六个筛选字段
            挤在同一个可换行的 flex 容器里，视口窄一点就可能被拆到不同行，
            各自按自己那行的基线对齐，看起来错位。独立成一行，永远互相对齐。 */}
        <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          <button type="button" className="btn btn--primary btn--sm" onClick={() => setFilters(draft)}>
            {t('audit.applyFilters')}
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              setDraft(EMPTY_FILTERS);
              setFilters(EMPTY_FILTERS);
            }}
          >
            {t('audit.clearFilters')}
          </button>
        </div>
      </div>

      {isPending ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <div className="empty-state">
          <p>{t('audit.empty')}</p>
        </div>
      ) : (
        <div className="table-scroll">
        <div className="table" style={{ display: 'block', minWidth: '820px' }}>
          <div className="data-row data-row--head">
            <span className="data-cell" style={{ width: '160px' }}>{t('audit.colOccurredAt')}</span>
            <span className="data-cell" style={{ width: '200px' }}>{t('audit.colAction')}</span>
            <span className="data-cell" style={{ width: '140px' }}>{t('audit.colEntityType')}</span>
            <span className="data-cell" style={{ width: '140px' }}>{t('audit.colActor')}</span>
            <span className="data-cell" style={{ width: '140px' }}>{t('audit.colAuthorizer')}</span>
          </div>
          <div ref={parentRef} style={{ maxHeight: '640px', overflow: 'auto' }}>
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className="data-row"
                style={{ height: `${ROW_HEIGHT}px` }}
                onClick={() => setSelected(item)}
              >
                {/* 定宽格超长省略（PRODUCT_CATEGORY_CREATED 曾溢进邻列），title 保全文 */}
                <span className="data-cell" style={{ width: '160px' }}>{fmt.format(new Date(item.occurredAt))}</span>
                <span className="data-cell" style={{ width: '200px' }} title={item.action}>{item.action}</span>
                <span className="data-cell" style={{ width: '140px' }} title={item.entityType}>{item.entityType}</span>
                <span className="data-cell" style={{ width: '140px' }} title={item.actorName ?? ''}>{item.actorName ?? '—'}</span>
                <span className="data-cell" style={{ width: '140px' }} title={item.authorizerName ?? ''}>{item.authorizerName ?? '—'}</span>
              </button>
            ))}
            {isFetchingNextPage && <p>{t('app.loading')}</p>}
          </div>
        </div>
        </div>
      )}

      {selected && <AuditDetailModal item={selected} onClose={() => setSelected(null)} />}
      {showOverrideReport && (
        <OverrideReportModal
          onClose={() => setShowOverrideReport(false)}
          onDrillDown={(authorizerName) => {
            setShowOverrideReport(false);
            setDraft((d) => ({ ...d, authorizerName }));
            setFilters((f) => ({ ...f, authorizerName }));
          }}
        />
      )}
    </div>
  );
}

function AuditDetailModal({
  item,
  onClose,
}: {
  readonly item: AuditLogListItem;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const { data, isPending } = useQuery({
    queryKey: ['audit-log', 'detail', item.id, item.occurredAt],
    queryFn: () =>
      apiFetch<AuditLogDetail>(
        `/audit-log/detail?id=${item.id}&occurredAt=${encodeURIComponent(item.occurredAt)}`,
      ),
  });

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
        <h2>{item.action}</h2>
        <p className="field-hint">
          {fmt.format(new Date(item.occurredAt))} · {item.entityType}
          {item.entityId ? ` (${item.entityId})` : ''}
        </p>
        {isPending || !data ? (
          <SkeletonBlock height={160} />
        ) : (
          <>
            <div className="field-hint">
              {t('audit.detailActor', { name: data.actorName ?? '—' })}
              {data.authorizerName && ` · ${t('audit.detailAuthorizer', { name: data.authorizerName })}`}
              {data.elevationMode !== 'NONE' && ` · ${data.elevationMode}`}
            </div>
            {data.reason && <p>{t('audit.detailReason', { reason: data.reason })}</p>}
            {data.diff && Object.keys(data.diff).length > 0 ? (
              <div className="table" style={{ display: 'block', marginTop: 'var(--space-3)' }}>
                <div className="row" style={{ fontWeight: 600 }}>
                  <span style={{ width: '160px' }}>{t('audit.diffField')}</span>
                  <span style={{ flex: 1 }}>{t('audit.diffFrom')}</span>
                  <span style={{ flex: 1 }}>{t('audit.diffTo')}</span>
                </div>
                {Object.entries(data.diff).map(([field, change]) => (
                  <div key={field} className="row" style={{ padding: 'var(--space-1) 0' }}>
                    <span style={{ width: '160px' }}>{field}</span>
                    <span style={{ flex: 1 }}>{JSON.stringify(change.from)}</span>
                    <span style={{ flex: 1 }}>{JSON.stringify(change.to)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {data.after !== null && data.after !== undefined && (
                  <pre style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--text-sm)' }}>
                    {JSON.stringify(data.after, null, 2)}
                  </pre>
                )}
              </>
            )}
          </>
        )}
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('app.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

function OverrideReportModal({
  onClose,
  onDrillDown,
}: {
  readonly onClose: () => void;
  readonly onDrillDown: (authorizerName: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
  const { data, isPending } = useQuery({
    queryKey: ['audit-log', 'override-report'],
    queryFn: () => apiFetch<{ items: OverrideReportRow[] }>('/audit-log/override-report'),
  });

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h2>{t('audit.overrideReportTitle')}</h2>
        <p className="field-hint">{t('audit.overrideReportHint')}</p>
        {isPending || !data ? (
          <SkeletonRows rows={3} rowHeight={36} bars={3} />
        ) : data.items.length === 0 ? (
          <p className="field-hint">{t('audit.overrideReportEmpty')}</p>
        ) : (
          <div className="table" style={{ display: 'block' }}>
            <div className="row" style={{ fontWeight: 600 }}>
              <span style={{ width: '180px' }}>{t('audit.colAuthorizer')}</span>
              <span style={{ width: '100px' }}>{t('audit.overrideCount')}</span>
              <span style={{ width: '180px' }}>{t('audit.overrideLastOccurred')}</span>
            </div>
            {data.items.map((row) => (
              <button
                key={row.authorizerUserId}
                type="button"
                className="data-row"
                onClick={() => row.authorizerName && onDrillDown(row.authorizerName)}
              >
                <span style={{ width: '180px' }}>{row.authorizerName ?? '—'}</span>
                <span style={{ width: '100px' }}>{row.overrideCount}</span>
                <span style={{ width: '180px' }}>{fmt.format(new Date(row.lastOccurredAt))}</span>
              </button>
            ))}
          </div>
        )}
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('app.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
